import { AuthLibInterfaces } from '@eventbrite/auth-lib';
import { gettext } from '@eventbrite/i18n';
import { ANALYTICS_CATEGORY_LOGIN } from '../constants';
import { GOOGLE_SIGNIN_METHOD } from '../constants/login';
import { LoginLibraryConfig } from '../models/login';

const getSoftAccountLookupConfig = () => ({
    getNotification: () => ({
        extraMessage: gettext(
            "<span class='eds-text-weight--heavy'>You haven't set a password for your account.</span> Continue as a guest for now, then add a password after you check out.",
        ),
    }),
});

const getSocialAccountLookupConfig = (onContinueAsGuest: () => void) => ({
    getNotification: (_props: { signInMethods?: string[] }) => {
        return _props.signInMethods?.includes(GOOGLE_SIGNIN_METHOD)
            ? {
                  extraMessage: gettext('Use the Google button to log in.'),
              }
            : {
                  extraMessage: gettext(
                      "Unfortunately isn't working right now.",
                  ),
                  ctaMessage: gettext('Please continue as a guest'),
                  ctaAction: () => {
                      onContinueAsGuest();
                  },
              };
    },
});

const getWithoutAcountLookupConfig = (onContinueAsGuest: () => void) => ({
    getNotification: () => ({
        extraMessage: gettext('Please check your mail info or'),
        ctaMessage: gettext('continue as a guest'),
        ctaAction: () => {
            onContinueAsGuest();
        },
    }),
});

export const getLoginLibraryConfiguration = ({
    captcha,
    gaSettings,
    onCheckoutLoginSuccessful,
    onContinueAsGuest,
    onDisplayNotification,
    onPasswordIncorrect,
    onVerificationRequired,
    request,
    ssoSettings,
}: LoginLibraryConfig) => ({
    disabledComponents: [AuthLibInterfaces.DisabledComponentsTypes.header],
    loginMethods: {
        attempt: () => onDisplayNotification(''),
        success: (response: { redirectUrl?: string } | undefined) => {
            if (response?.redirectUrl?.includes('issue_mfa')) {
                onDisplayNotification('STAFF_USER_NOT_ALLOWED');
            } else {
                onCheckoutLoginSuccessful();
            }
        },
        error: {
            ['ACCESS_DENIED']: { action: () => onPasswordIncorrect() },
            ['VERIFICATION_REQUIRED']: {
                action: () => onVerificationRequired(),
            },
        },
    },
    lookupMethods: {
        attempt: () => onDisplayNotification(''),
        success: {
            WITH_USER_ID: {
                SOCIAL_ACCOUNT: getSocialAccountLookupConfig(onContinueAsGuest),
                SOFT_ACCOUNT: getSoftAccountLookupConfig(),
            },
            WITHOUT_USER_ID: getWithoutAcountLookupConfig(onContinueAsGuest),
        },
    },
    analytics: {
        categoryOverride: ANALYTICS_CATEGORY_LOGIN,
        request,
        gaSettings,
    },
    captcha,
    social: {
        SSOSettings: ssoSettings,
        disabledOptions: [
            AuthLibInterfaces.DisabledSocialOptions.apple,
            AuthLibInterfaces.DisabledSocialOptions.facebook,
        ],
    },
    socialMethods: {
        attempt: () => onDisplayNotification(''),
        success: () => onCheckoutLoginSuccessful(),
        error: {
            default: (error: any) => onDisplayNotification(error.__all__[0]),
        },
    },
    featureFlags: {
        launchGoogleOneTapWeb: true,
    },
    isCheckoutAuthenticationFlow: true,
});

export const showNewLoginComponent = (isEventbriteTLD?: boolean) =>
    !!isEventbriteTLD;

export const getIframeLoginUrl = (locale?: string) => {
    const loginUrl = new URL('/signin/checkout', window.location.origin);
    if (locale) {
        loginUrl.searchParams.append('locale', locale);
    }
    loginUrl.searchParams.append('__view', 'embedded');
    loginUrl.searchParams.append('stld', '1');

    return loginUrl.href;
};

export const getLoginWindowPopUpInstance = (locale?: string) => {
    // Align the window
    const POPUP_WINDOW_WIDTH = 700;
    const POPUP_WINDOW_HEIGHT = 700;
    const left = (screen.width - POPUP_WINDOW_WIDTH) / 2;
    const top = (screen.height - POPUP_WINDOW_HEIGHT) / 2;
    const logInWindow = window.open(
        getIframeLoginUrl(locale),
        'EventbriteLoginPopup',
        `height=${POPUP_WINDOW_HEIGHT}, width=${POPUP_WINDOW_WIDTH}, ` +
            `top=${top}, left=${left}`,
    );

    return logInWindow;
};
